/**
 * AI Design Assistant Panel
 * Allows users to generate web pages, LinkedIn posts, flyers, and emails
 * using Claude AI, then apply the output to the page editor or copy it.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Sparkles,
  Send,
  RefreshCw,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  Globe,
  Target,
  Linkedin,
  FileText,
  Mail,
  Share2,
  ArrowRight,
  History,
  Wand2,
  X,
  Loader2,
  Plus,
  Search,
  Layers,
  MessageSquare,
  Mic,
} from 'lucide-react';
import type {
  AIContentType,
  AIGeneration,
  AIGeneratedBlocks,
  AIGeneratedCopy,
  AIOutputFormat,
  ContentBlock,
} from '../../types';
import { AI_CONTENT_TYPE_OPTIONS } from '../../types';
import { webPresenceApi } from '../../../../shared/services/platformApi';
import { normalizeAIBlocks } from '../../utils/normalizeAIBlocks';
import { PublicSiteBlock, PUBLIC_DEFAULT_THEME } from '../../../../admin/pages/PublicBlockRenderer';

// ============================================================================
// TYPES
// ============================================================================

interface AIDesignAssistantProps {
  siteId?: string;
  pageId?: string;
  onApplyBlocks?: (blocks: ContentBlock[]) => void;
  /**
   * Fired with the generated page blocks as soon as a `blocks` generation (or
   * refinement) completes — used to drive an external live-preview surface such
   * as the Studio's right-hand column. Side-effect-free (does not create/apply a
   * page); that stays the job of `onApplyBlocks`. Called with `[]` when the user
   * starts a new generation, so the surface can reset.
   */
  onPreview?: (blocks: ContentBlock[]) => void;
  onApplySeo?: (seo: { metaTitle?: string; metaDescription?: string }) => void;
  onSaveAsOutput?: (generation: AIGeneration, navigateTo: string) => void;
  isOpen: boolean;
  onClose: () => void;
  /** When 'comms', hides web_page and landing_page from content type selector */
  mode?: 'web' | 'comms';
  /** Pre-fill the prompt textarea (one-shot, used by TrivenyHome handoff). */
  initialPrompt?: string;
  /** Pre-select a content type when seeded from a launcher (e.g. 'landing_page'). */
  initialContentType?: AIContentType;
}

type PanelView = 'generate' | 'result' | 'history';

interface GenerationState {
  loading: boolean;
  error: string | null;
  currentGeneration: AIGeneration | null;
  history: AIGeneration[];
}

// ============================================================================
// CONTENT TYPE ICONS
// ============================================================================

const CONTENT_TYPE_ICONS: Record<AIContentType, React.ReactNode> = {
  web_page: <Globe size={18} />,
  landing_page: <Target size={18} />,
  linkedin_post: <Linkedin size={18} />,
  flyer: <FileText size={18} />,
  email: <Mail size={18} />,
  social_post: <Share2 size={18} />,
  sms: <MessageSquare size={18} />,
  voice_ai_script: <Mic size={18} />,
};

// ============================================================================
// COMPONENT
// ============================================================================

export function AIDesignAssistant({
  siteId,
  pageId,
  onApplyBlocks,
  onPreview,
  onApplySeo,
  onSaveAsOutput,
  isOpen,
  onClose,
  mode = 'web',
  initialPrompt,
  initialContentType,
}: AIDesignAssistantProps) {
  const isCommsMode = mode === 'comms';
  // State
  const [view, setView] = useState<PanelView>('generate');
  const [contentType, setContentType] = useState<AIContentType>(
    initialContentType || (isCommsMode ? 'email' : 'web_page'),
  );
  const [prompt, setPrompt] = useState(initialPrompt || '');
  const [refineFeedback, setRefineFeedback] = useState('');
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generation, setGeneration] = useState<GenerationState>({
    loading: false,
    error: null,
    currentGeneration: null,
    history: [],
  });

  // Context options
  const [tone, setTone] = useState('professional');
  const [audience, setAudience] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  // AI model choice. 'fast' → Claude Sonnet 4.6, 'quality' → Claude Opus 4.8.
  const [modelPref, setModelPref] = useState<'fast' | 'quality'>('fast');
  const MODEL_LABELS: Record<'fast' | 'quality', string> = {
    fast: 'Claude Sonnet 4.6',
    quality: 'Claude Opus 4.8',
  };

  const promptRef = useRef<HTMLTextAreaElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  // Tracks whether we've already auto-fired generation for the current
  // initialPrompt. Without this guard a re-render with the same seed value
  // would re-trigger generation.
  const autoFiredRef = useRef(false);

  // Auto-focus prompt on open
  useEffect(() => {
    if (isOpen && promptRef.current) {
      setTimeout(() => promptRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Load history on mount
  useEffect(() => {
    if (isOpen) {
      loadHistory();
    }
  }, [isOpen]);

  // ── Handlers ─────────────────────────────────────────────────────────

  const loadHistory = useCallback(async () => {
    try {
      const params: Record<string, unknown> = { limit: 20 };
      if (pageId) params.page_id = pageId;
      const res = await webPresenceApi.aiGetGenerations(params as any);
      if (res.success && res.data) {
        setGeneration(prev => ({ ...prev, history: res.data || [] }));
      }
    } catch {
      // Silently fail — history is non-critical
    }
  }, [pageId]);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim() || generation.loading) return;

    setGeneration(prev => ({ ...prev, loading: true, error: null }));
    setView('result');

    try {
      const context: Record<string, unknown> = {};
      if (tone) context.tone = tone;
      if (audience) context.audience = audience;

      const res = await webPresenceApi.aiGenerate({
        content_type: contentType,
        prompt: prompt.trim(),
        context: Object.keys(context).length > 0 ? context : undefined,
        model_preference: modelPref,
        site_id: siteId,
        page_id: pageId,
      });

      if (res.success && res.data) {
        const gen: AIGeneration = {
          id: res.data.id,
          siteId: siteId || null,
          pageId: pageId || null,
          contentType: res.data.content_type,
          outputFormat: res.data.output_format,
          prompt,
          refinementOf: null,
          generated: res.data.generated,
          meta: res.data.meta,
          status: 'completed',
          appliedToPageId: null,
          appliedAt: null,
          createdAt: res.data.created_at,
        };
        setGeneration(prev => ({
          ...prev,
          loading: false,
          currentGeneration: gen,
          history: [gen, ...prev.history],
        }));
      } else {
        setGeneration(prev => ({
          ...prev,
          loading: false,
          error: res.error?.message || 'Generation failed',
        }));
      }
    } catch (err: any) {
      setGeneration(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'An error occurred',
      }));
    }
  }, [prompt, contentType, tone, audience, modelPref, siteId, pageId, generation.loading]);

  // Auto-fire generation when an `initialPrompt` was handed off from another
  // surface (e.g. TrivenyHome's Design pillar modal). The prompt is already
  // pre-filled via useState, so the user sees the text + the result panel
  // appear without an extra click. Single-shot per mount.
  useEffect(() => {
    if (!isOpen) return;
    if (autoFiredRef.current) return;
    if (!initialPrompt || !initialPrompt.trim()) return;
    // Defer one tick so the textarea and content type are committed before
    // handleGenerate reads them via closure.
    const t = window.setTimeout(() => {
      autoFiredRef.current = true;
      handleGenerate();
    }, 60);
    return () => window.clearTimeout(t);
  }, [isOpen, initialPrompt, handleGenerate]);

  const handleRefine = useCallback(async () => {
    if (!refineFeedback.trim() || !generation.currentGeneration || generation.loading) return;

    setGeneration(prev => ({ ...prev, loading: true, error: null }));

    try {
      const res = await webPresenceApi.aiRefine({
        generation_id: generation.currentGeneration.id,
        feedback: refineFeedback.trim(),
      });

      if (res.success && res.data) {
        const gen: AIGeneration = {
          id: res.data.id,
          siteId: siteId || null,
          pageId: pageId || null,
          contentType: res.data.content_type,
          outputFormat: res.data.output_format,
          prompt: refineFeedback,
          refinementOf: res.data.refinement_of,
          generated: res.data.generated,
          meta: res.data.meta,
          status: 'completed',
          appliedToPageId: null,
          appliedAt: null,
          createdAt: res.data.created_at,
        };
        setGeneration(prev => ({
          ...prev,
          loading: false,
          currentGeneration: gen,
          history: [gen, ...prev.history],
        }));
        setRefineFeedback('');
      } else {
        setGeneration(prev => ({
          ...prev,
          loading: false,
          error: res.error?.message || 'Refinement failed',
        }));
      }
    } catch (err: any) {
      setGeneration(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'An error occurred',
      }));
    }
  }, [refineFeedback, generation.currentGeneration, generation.loading, siteId, pageId]);

  const [applied, setApplied] = useState(false);

  const getGeneratedBlocks = useCallback((): ContentBlock[] => {
    const gen = generation.currentGeneration;
    if (!gen) return [];
    const data = gen.generated as any;
    let blocks = data?.blocks;
    if (typeof blocks === 'string') {
      try { blocks = JSON.parse(blocks); } catch { blocks = []; }
    }
    return Array.isArray(blocks) ? blocks : [];
  }, [generation.currentGeneration]);

  // Push generated page blocks to an external preview surface (the Studio's
  // right-hand column) as soon as a generation/refinement completes, so the live
  // preview appears there rather than cramped below the prompt in this panel.
  // Guarded by the generation id: `onPreview` is typically an inline callback
  // (new identity each parent render) that sets parent state, so without this
  // ref the effect would re-fire on every re-render and loop indefinitely.
  const previewedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!onPreview) return;
    const gen = generation.currentGeneration;
    if (!gen || gen.outputFormat !== 'blocks') return;
    if (previewedIdRef.current === gen.id) return;
    const blocks = getGeneratedBlocks();
    if (blocks.length > 0) {
      previewedIdRef.current = gen.id;
      onPreview(blocks);
    }
  }, [generation.currentGeneration, onPreview, getGeneratedBlocks]);

  const handleApplyToPage = useCallback(() => {
    const gen = generation.currentGeneration;
    if (!gen) return;

    const blocks = getGeneratedBlocks();
    if (blocks.length > 0 && onApplyBlocks) {
      onApplyBlocks(blocks);
      setApplied(true);
      setTimeout(() => setApplied(false), 3000);
    }

    const data = gen.generated as AIGeneratedBlocks;
    if (onApplySeo && data?.seo) {
      onApplySeo(data.seo);
    }
  }, [generation.currentGeneration, onApplyBlocks, onApplySeo, getGeneratedBlocks]);

  const handleCopyText = useCallback(async () => {
    const gen = generation.currentGeneration;
    if (!gen) return;

    let text = '';
    if (gen.outputFormat === 'copy') {
      const data = gen.generated as AIGeneratedCopy;
      text = data.copy || '';
      if (data.hashtags?.length) {
        text += '\n\n' + data.hashtags.join(' ');
      }
    } else {
      text = JSON.stringify(gen.generated, null, 2);
    }

    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [generation.currentGeneration]);

  const handleSelectHistoryItem = useCallback((item: any) => {
    // Safely parse generated_blocks — could be a JSON string, array, or null
    let blocks = item.generated_blocks;
    if (typeof blocks === 'string') {
      try { blocks = JSON.parse(blocks); } catch { blocks = []; }
    }
    if (!Array.isArray(blocks)) blocks = [];

    let seo = item.generated_seo;
    if (typeof seo === 'string') {
      try { seo = JSON.parse(seo); } catch { seo = {}; }
    }

    const isBlockType = item.content_type === 'web_page' || item.content_type === 'landing_page' || item.content_type === 'flyer';

    const gen: AIGeneration = {
      id: item.id,
      siteId: item.site_id || null,
      pageId: item.page_id || null,
      contentType: item.content_type,
      outputFormat: isBlockType ? 'blocks' : 'copy',
      prompt: item.prompt,
      refinementOf: item.refinement_of || null,
      generated: isBlockType && blocks.length > 0
        ? { blocks, seo: seo || {} }
        : { copy: item.generated_copy || '' },
      meta: {
        model: item.ai_model,
        input_tokens: item.input_tokens,
        output_tokens: item.output_tokens,
        duration_ms: item.duration_ms,
      },
      status: item.status,
      appliedToPageId: item.applied_to_page_id || null,
      appliedAt: item.applied_at || null,
      createdAt: item.created_at,
    };
    setGeneration(prev => ({ ...prev, currentGeneration: gen }));
    setView('result');
  }, []);

  const handleNewGeneration = useCallback(() => {
    setView('generate');
    setPrompt('');
    setRefineFeedback('');
    setGeneration(prev => ({ ...prev, currentGeneration: null, error: null }));
    onPreview?.([]);
  }, [onPreview]);

  // ── Keyboard shortcut ────────────────────────────────────────────────

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (view === 'generate') handleGenerate();
      else if (view === 'result') handleRefine();
    }
  }, [view, handleGenerate, handleRefine]);

  if (!isOpen) return null;

  const selectedTypeOption = AI_CONTENT_TYPE_OPTIONS.find(o => o.value === contentType)!;

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 w-[400px] shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-violet-600 dark:text-violet-400" />
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">AI Design Assistant</h3>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setView('history')}
            className={`p-1.5 rounded-md transition-colors ${
              view === 'history' ? 'bg-violet-100 text-violet-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }`}
            title="Generation history"
          >
            <History size={16} />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Content Type Selector */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <div className="relative">
          <button
            onClick={() => setShowTypeSelector(!showTypeSelector)}
            className="flex items-center justify-between w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-lg hover:border-violet-300 transition-colors"
          >
            <div className="flex items-center gap-2">
              {CONTENT_TYPE_ICONS[contentType]}
              <span className="font-medium">{selectedTypeOption.label}</span>
              <span className="text-xs text-gray-400">{selectedTypeOption.description}</span>
            </div>
            <ChevronDown size={14} className="text-gray-400" />
          </button>

          {showTypeSelector && (
            <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg py-1">
              {AI_CONTENT_TYPE_OPTIONS
                .filter(option => !isCommsMode || (option.value !== 'web_page' && option.value !== 'landing_page'))
                .map(option => (
                <button
                  key={option.value}
                  onClick={() => {
                    setContentType(option.value);
                    setShowTypeSelector(false);
                  }}
                  className={`flex items-center gap-3 w-full px-3 py-2 text-sm hover:bg-violet-50 transition-colors ${
                    contentType === option.value ? 'bg-violet-50 text-violet-700' : 'text-gray-700'
                  }`}
                >
                  {CONTENT_TYPE_ICONS[option.value]}
                  <div className="text-left">
                    <div className="font-medium">{option.label}</div>
                    <div className="text-xs text-gray-400">{option.description}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Panel Area */}
      <div className="flex-1 overflow-y-auto" onKeyDown={handleKeyDown}>
        {/* ── Generate View ──────────────────────────────────── */}
        {view === 'generate' && (
          <div className="p-4 space-y-4">
            {/* Prompt Input */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Describe what you want to create
              </label>
              <textarea
                ref={promptRef}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder={getPlaceholder(contentType)}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-transparent transition-all"
                rows={5}
              />
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-gray-400">{prompt.length}/5000</span>
                <span className="text-xs text-gray-400">
                  {navigator.platform.includes('Mac') ? '\u2318' : 'Ctrl'}+Enter to generate
                </span>
              </div>
            </div>

            {/* Advanced Options */}
            <div>
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showAdvanced ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                Advanced options
              </button>

              {showAdvanced && (
                <div className="mt-2 space-y-3 pl-2 border-l-2 border-gray-100">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Tone</label>
                    <select
                      value={tone}
                      onChange={e => setTone(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-violet-300"
                    >
                      <option value="professional">Professional</option>
                      <option value="warm">Warm & Friendly</option>
                      <option value="formal">Formal</option>
                      <option value="casual">Casual</option>
                      <option value="urgent">Urgent / Action-oriented</option>
                      <option value="empathetic">Empathetic & Supportive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Target Audience</label>
                    <input
                      type="text"
                      value={audience}
                      onChange={e => setAudience(e.target.value)}
                      placeholder="e.g., community members, funders, partners"
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-violet-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">AI model</label>
                    <select
                      value={modelPref}
                      onChange={e => setModelPref(e.target.value as 'fast' | 'quality')}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-violet-300"
                    >
                      <option value="fast">Fast — {MODEL_LABELS.fast}</option>
                      <option value="quality">Highest quality — {MODEL_LABELS.quality}</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Model reference — always visible so the user knows what's generating. */}
            <p className="text-[11px] text-gray-400 -mt-1">
              Generates with <span className="font-medium text-gray-500">{MODEL_LABELS[modelPref]}</span>
            </p>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={!prompt.trim() || generation.loading}
              className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {generation.loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Wand2 size={16} />
                  Generate {selectedTypeOption.label}
                </>
              )}
            </button>

            {generation.error && (
              <div className="px-3 py-2 text-xs text-red-700 bg-red-50 rounded-lg border border-red-200">
                {generation.error}
              </div>
            )}
          </div>
        )}

        {/* ── Result View ────────────────────────────────────── */}
        {view === 'result' && (
          <div className="p-4 space-y-4" ref={resultRef}>
            {generation.loading ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="relative mb-4">
                  <Sparkles size={32} className="text-violet-400 animate-pulse" />
                  <Loader2 size={16} className="absolute -bottom-1 -right-1 text-violet-600 animate-spin" />
                </div>
                <p className="text-sm font-medium text-gray-700">Creating your {selectedTypeOption.label.toLowerCase()}...</p>
                <p className="text-xs text-gray-400 mt-1">This usually takes 5-15 seconds</p>
              </div>
            ) : generation.currentGeneration ? (
              <>
                {/* Result Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {CONTENT_TYPE_ICONS[generation.currentGeneration.contentType]}
                    <span className="text-sm font-medium text-gray-700">
                      {AI_CONTENT_TYPE_OPTIONS.find(o => o.value === generation.currentGeneration?.contentType)?.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <span>{((generation.currentGeneration.meta.duration_ms || 0) / 1000).toFixed(1)}s</span>
                    <span>&middot;</span>
                    <span>{generation.currentGeneration.meta.output_tokens} tokens</span>
                  </div>
                </div>

                {/* Result Content. Page-block output is previewed on an external
                    surface (the Studio's right column) when `onPreview` is wired;
                    otherwise fall back to the inline thumbnail. Copy output always
                    shows inline. */}
                {generation.currentGeneration.outputFormat === 'blocks' ? (
                  onPreview ? (
                    <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-500 bg-violet-50 border border-violet-100 rounded-lg">
                      <Sparkles size={14} className="text-violet-400 flex-shrink-0" />
                      <span>Live preview is shown on the right.</span>
                    </div>
                  ) : (
                    <BlocksPreview generated={generation.currentGeneration.generated as AIGeneratedBlocks} />
                  )
                ) : (
                  <CopyPreview generated={generation.currentGeneration.generated as AIGeneratedCopy} />
                )}

                {/* Content-type-aware Action Buttons */}
                <div className="flex flex-col gap-2">
                  {/* Primary action per content type */}
                  {(() => {
                    const ct = generation.currentGeneration!.contentType;
                    const gen = generation.currentGeneration!;
                    if (ct === 'web_page' || ct === 'landing_page') {
                      return onApplyBlocks && getGeneratedBlocks().length > 0 ? (
                        <button onClick={handleApplyToPage} disabled={applied}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${applied ? 'bg-green-600 text-white' : 'bg-violet-600 text-white hover:bg-violet-700'}`}>
                          {applied ? <><Check size={14} /> Applied!</> : <><Layers size={14} /> Apply to Page</>}
                        </button>
                      ) : null;
                    }
                    if (ct === 'flyer') {
                      return (
                        <div className="flex gap-2">
                          {onApplyBlocks && getGeneratedBlocks().length > 0 && (
                            <button onClick={handleApplyToPage}
                              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700">
                              <Layers size={14} /> Edit in Page Builder
                            </button>
                          )}
                          {onSaveAsOutput && (
                            <button onClick={() => onSaveAsOutput(gen, 'flyers')}
                              className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-violet-700 bg-violet-100 rounded-lg hover:bg-violet-200">
                              Save as Flyer
                            </button>
                          )}
                        </div>
                      );
                    }
                    if (ct === 'email') {
                      return onSaveAsOutput ? (
                        <button onClick={() => onSaveAsOutput(gen, 'emails')}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700">
                          <ArrowRight size={14} /> Save to Emails
                        </button>
                      ) : null;
                    }
                    // linkedin_post, social_post
                    return onSaveAsOutput ? (
                      <button onClick={() => onSaveAsOutput(gen, 'posts')}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700">
                        <ArrowRight size={14} /> Save to Posts
                      </button>
                    ) : null;
                  })()}

                  {/* Secondary actions row */}
                  <div className="flex gap-2">
                    <button onClick={handleCopyText}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                      {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                    <button onClick={handleNewGeneration}
                      className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                      <Plus size={14} /> New
                    </button>
                  </div>
                </div>

                {/* Refinement Input */}
                <div className="border-t border-gray-100 pt-3">
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Refine this output
                  </label>
                  <div className="flex gap-2">
                    <textarea
                      value={refineFeedback}
                      onChange={e => setRefineFeedback(e.target.value)}
                      placeholder="e.g., Make the headline more urgent, add a testimonials section..."
                      className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-transparent"
                      rows={2}
                    />
                    <button
                      onClick={handleRefine}
                      disabled={!refineFeedback.trim() || generation.loading}
                      className="self-end p-2 text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Refine"
                    >
                      <RefreshCw size={16} />
                    </button>
                  </div>
                </div>
              </>
            ) : generation.error ? (
              <div className="text-center py-8">
                <p className="text-sm text-red-600 mb-3">{generation.error}</p>
                <button
                  onClick={() => { setView('generate'); setGeneration(prev => ({ ...prev, error: null })); }}
                  className="text-sm text-violet-600 hover:text-violet-700 font-medium"
                >
                  Try again
                </button>
              </div>
            ) : null}
          </div>
        )}

        {/* ── History View ───────────────────────────────────── */}
        {view === 'history' && (
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700">Generation History</h4>
              <button
                onClick={handleNewGeneration}
                className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 font-medium"
              >
                <Plus size={12} /> New
              </button>
            </div>

            {generation.history.length === 0 ? (
              <div className="text-center py-8">
                <Sparkles size={24} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm text-gray-400">No generations yet</p>
                <p className="text-xs text-gray-400 mt-1">Create your first AI-generated content above</p>
              </div>
            ) : (
              generation.history.map((item: any) => (
                <button
                  key={item.id}
                  onClick={() => handleSelectHistoryItem(item)}
                  className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-violet-300 hover:bg-violet-50/50 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    {CONTENT_TYPE_ICONS[(item.contentType || item.content_type) as AIContentType]}
                    <span className="text-xs font-medium text-gray-600 capitalize">
                      {(item.contentType || item.content_type || '').replace('_', ' ')}
                    </span>
                    <span className={`ml-auto text-xs px-1.5 py-0.5 rounded-full ${
                      item.status === 'applied' ? 'bg-green-100 text-green-700'
                        : item.status === 'completed' ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {item.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-2">{item.prompt}</p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                    <span>{new Date(item.createdAt || item.created_at).toLocaleDateString()}</span>
                    {item.meta?.duration_ms || item.duration_ms ? (
                      <span>&middot; {(((item.meta?.duration_ms || item.duration_ms) || 0) / 1000).toFixed(1)}s</span>
                    ) : null}
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function BlocksPreview({ generated }: { generated: AIGeneratedBlocks }) {
  const rawBlocks = Array.isArray(generated.blocks) ? generated.blocks : [];
  // Normalize to the editor block shape, then render with the SAME renderer the
  // public site uses — so the preview matches what will actually ship.
  const blocks = normalizeAIBlocks(rawBlocks as any[], '');

  return (
    <div className="space-y-2">
      {generated.seo?.metaTitle && (
        <div className="px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
          <div className="text-xs font-medium text-blue-600 mb-1">SEO</div>
          <div className="text-sm font-medium text-blue-900">{generated.seo.metaTitle}</div>
          {generated.seo.metaDescription && (
            <div className="text-xs text-blue-700 mt-0.5">{generated.seo.metaDescription}</div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between text-xs font-medium text-gray-500 mt-2">
        <span>{blocks.length} content blocks</span>
        <span className="text-gray-400">Live preview</span>
      </div>

      {blocks.length > 0 ? (
        // Desktop-width canvas scaled down to a thumbnail (zoom keeps the layout
        // box in proportion so the narrow panel shows a true-to-life preview).
        <div
          className="border border-gray-200 rounded-lg overflow-auto bg-white"
          style={{ maxHeight: 440 }}
        >
          <div style={{ zoom: 0.45, width: '222%' }}>
            {blocks.map((b, i) => (
              <PublicSiteBlock key={b.id || i} block={b as any} t={PUBLIC_DEFAULT_THEME} />
            ))}
          </div>
        </div>
      ) : (
        <div className="px-3 py-6 text-center text-xs text-gray-400 border border-dashed rounded-lg">
          No blocks to preview yet.
        </div>
      )}
    </div>
  );
}

function CopyPreview({ generated }: { generated: AIGeneratedCopy }) {
  return (
    <div className="space-y-3">
      {/* Subject line (email) */}
      {generated.subject && (
        <div className="px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
          <div className="text-xs font-medium text-amber-600 mb-0.5">Subject</div>
          <div className="text-sm font-medium text-amber-900">{generated.subject}</div>
          {generated.previewText && (
            <div className="text-xs text-amber-700 mt-0.5">{generated.previewText}</div>
          )}
        </div>
      )}

      {/* Main copy */}
      <div className="px-3 py-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">
          {generated.copy}
        </div>
      </div>

      {/* Hashtags */}
      {generated.hashtags && generated.hashtags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {generated.hashtags.map((tag, i) => (
            <span key={i} className="px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-full">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* CTA */}
      {(generated.callToAction || generated.ctaText) && (
        <div className="px-3 py-2 bg-violet-50 rounded-lg border border-violet-100">
          <div className="text-xs font-medium text-violet-600 mb-0.5">Call to Action</div>
          <div className="text-sm text-violet-800">{generated.callToAction || generated.ctaText}</div>
        </div>
      )}

      {/* Image suggestion */}
      {generated.suggestedImagePrompt && (
        <div className="px-3 py-2 bg-green-50 rounded-lg border border-green-100">
          <div className="text-xs font-medium text-green-600 mb-0.5">Suggested Image</div>
          <div className="text-xs text-green-700">{generated.suggestedImagePrompt}</div>
        </div>
      )}

      {/* Send time */}
      {generated.suggestedSendTime && (
        <div className="text-xs text-gray-400">
          Suggested send time: {generated.suggestedSendTime}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function getPlaceholder(contentType: AIContentType): string {
  const placeholders: Record<AIContentType, string> = {
    web_page: 'Create a page promoting our housing assistance program. Include a hero section, key benefits, eligibility info, and a contact form CTA.',
    landing_page: 'Build a landing page for our Fall enrollment drive targeting Medi-Cal eligible families. Focus on CalAIM ECM benefits.',
    linkedin_post: 'Write a post highlighting our organization\'s impact: 500 families served, 85% enrollment retention, new partnership with County Health.',
    flyer: 'Create a flyer for our free health screening event on Saturday March 15 at the community center. Include event details and registration info.',
    email: 'Draft an outreach email to members about our new transportation assistance benefit. Emphasize ease of enrollment.',
    social_post: 'Announce our new telehealth service launch — available to all enrolled members starting next week.',
  };
  return placeholders[contentType];
}

export default AIDesignAssistant;
